import * as React from 'react';

import {
  DescriptionPage_CourseType as DescriptionPageCourseType,
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
} from '__generated__/graphql-types';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import StyledCoachTriggerButtonWithAnimationOnMount from 'bundles/unified-common/components/coach-on-discovery/StyledCoachTriggerButtonWithAnimationOnMount';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import useXdpContextConfig from 'bundles/unified-description-page-common/components/coach/useXdpContextConfig';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useCoachCareerFitAssistantData } from 'bundles/unified-description-page-common/providers/specialization/CoachCareerFitAssistantProvider';

import _t from 'i18n!nls/xdp';

// New "Coach on all XDPs" behavior, gated on `extend-coach-on-xdp-gate`: V3 conversational discovery
// opening on a welcome message, with typed-message routing for follow-ups.
const ROLLOUT_CONFIG = {
  coachModeId: 'XDP_CAREER_FIT_ASSISTANT_V3',
  buttonActionId: 'WELCOME_MESSAGE',
  routerActionId: 'ROUTER',
} as const;

// Original behavior (logged-in s12n/prof-cert only): V2 career-fit assistant routed through ROUTER.
const LEGACY_CONFIG = {
  coachModeId: 'XDP_CAREER_FIT_ASSISTANT_V2',
  buttonActionId: 'IS_THIS_RIGHT_FOR_ME',
  routerActionId: 'ROUTER',
} as const;

const CoachButton = ({ className }: { className?: string }) => {
  const { coachCareerFitAssistantEnabled, extendedCoachOnXdpRolloutEnabled } = useCoachCareerFitAssistantData();
  const config = extendedCoachOnXdpRolloutEnabled ? ROLLOUT_CONFIG : LEGACY_CONFIG;

  const contextConfig = useXdpContextConfig({
    coachModeId: config.coachModeId,
    coachActionId: config.buttonActionId,
  });

  const { setCoachOpen, setContextConfig } = useCoachState();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: 'coach_trigger_button',
      linkName: `${config.coachModeId}_${config.buttonActionId}`.toLowerCase(),
    },
  });

  const specializationPageData = useSpecializationPageData();
  const coursePageData = useCoursePageData();

  if (!coachCareerFitAssistantEnabled || !contextConfig) {
    return null;
  }

  // Guided (RHYME_PROJECT) and portfolio (PROJECT) variants are both projects under the hood.
  const isProject =
    coursePageData?.courseType === DescriptionPageCourseType.Project ||
    coursePageData?.courseType === DescriptionPageCourseType.RhymeProject;

  const label = _t('Is this right for me?');
  let ariaLabel;
  if (specializationPageData?.productVariant === DescriptionPageProductVariant.ProfessionalCertificate) {
    ariaLabel = _t('Ask Coursera: is this certificate right for me?');
  } else if (specializationPageData) {
    ariaLabel = _t('Ask Coursera: is this specialization right for me?');
  } else if (isProject) {
    ariaLabel = _t('Ask Coursera: is this project right for me?');
  } else {
    ariaLabel = _t('Ask Coursera: is this course right for me?');
  }

  return (
    <div ref={visibilityTrackingRef}>
      <ExternalCoachAction
        size="small"
        key="coach"
        newSessionOnClick={false}
        onClick={() => {
          const { message, ...coachChatContext } = contextConfig;
          setContextConfig({
            ...coachChatContext,
            config: {
              coachModeId: config.coachModeId,
              coachActionId: config.routerActionId,
            },
          });
          setCoachOpen(true);
        }}
        aria-label={ariaLabel}
        className={className}
        label={label}
        enableTextStreaming={extendedCoachOnXdpRolloutEnabled}
        enableLoggedOutMode={extendedCoachOnXdpRolloutEnabled}
        {...contextConfig}
        message={label}
        customCoachTriggerButton={StyledCoachTriggerButtonWithAnimationOnMount}
      />
    </div>
  );
};

export default CoachButton;
